import { useState } from 'react';
import {
  UserGroupIcon,
  DocumentCheckIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BriefcaseIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const VistaPanoramica = () => {
  const [timeRange, setTimeRange] = useState('30D');
  const [dateRange] = useState('Jan 01, 2025 - Feb 20, 2025');

  const stats = [
    {
      name: 'Estudiantes Activos',
      value: '15',
      icon: UserGroupIcon,
      change: '+12.5%',
      changeType: 'increase',
      description: 'estudiantes usando Worky'
    },
    {
      name: 'Entrevistas Simuladas',
      value: '847',
      icon: BriefcaseIcon,
      change: '+22.3%',
      changeType: 'increase',
      description: 'en el último mes'
    },
    {
      name: 'CVs Analizados',
      value: '1,203',
      icon: DocumentTextIcon,
      change: '+18.2%',
      changeType: 'increase',
      description: 'con feedback detallado'
    },
    {
      name: 'Tasa de Mejora',
      value: '76%',
      icon: ChartBarIcon,
      change: '+5.4%',
      changeType: 'increase',
      description: 'en habilidades blandas'
    },
  ];

  const ciclosData = [
    { name: 'Ciclo 1', value: 25 },
    { name: 'Ciclo 2', value: 35 },
    { name: 'Ciclo 3', value: 30 },
    { name: 'Ciclo 4', value: 40 },
  ];

  const mejorPerformance = [
    { name: 'Juan Pablo Cepeda', value: 92 },
    { name: 'Diego Alonzo Chá', value: 80 },
    { name: 'Diego Rodriguez F', value: 73 },
    { name: 'Francesco Lucchesi', value: 55 },
    { name: 'Vincenzo Lucchesi', value: 32 },
  ];

  const peorPerformance = [
    { name: 'Demo Ucal', value: 8 },
    { name: 'Erika Palacios', value: 20 },
    { name: 'Valeria Alexandra', value: 28 },
    { name: 'Nayeli Camilla Mo', value: 28 },
    { name: 'Camila Orbegoz', value: 31 },
  ];

  const puntajePromedio = [
    { name: 'Ciencias de la\nComunicación', value: 85, color: '#024579' },
    { name: 'Arquitectura', value: 78, color: '#028bbf' },
    { name: 'Marketing', value: 72, color: '#00bf63' },
    { name: 'Administración', value: 68, color: '#024579' },
    { name: 'Psicología', value: 65, color: '#028bbf' }
  ].sort((a, b) => b.value - a.value); // Ordenar de mayor a menor

  const aspectosEntrevista = [
    { name: 'Ejemplos STAR concretos', value: 80 },
    { name: 'Preguntas al entrevistador', value: 75 },
    { name: 'Comunicación verbal clara', value: 70 },
    { name: 'Control de nervios/ansiedad', value: 68 },
    { name: 'Manejo de preguntas difíciles', value: 65 },
    { name: 'Lenguaje corporal profesional', value: 55 }
  ].sort((a, b) => b.value - a.value);

  const aspectosCV = [
    { name: 'Logros cuantificables', value: 85 },
    { name: 'Experiencia relevante destacada', value: 78 },
    { name: 'Palabras clave del sector', value: 72 },
    { name: 'Objetivos profesionales claros', value: 70 },
    { name: 'Formato profesional', value: 60 },
    { name: 'Ortografía y gramática', value: 45 }
  ].sort((a, b) => b.value - a.value);

  const areasInteres = [
    { name: 'Marketing Digital y Publicidad', value: 32, color: '#024579' },
    { name: 'Diseño y Artes Creativas', value: 28, color: '#028bbf' },
    { name: 'Negocios y Emprendimiento', value: 25, color: '#00bf63' },
    { name: 'Tecnología y Desarrollo', value: 20, color: '#024579' },
    { name: 'Gestión de Proyectos', value: 18, color: '#028bbf' },
    { name: 'Comunicación Corporativa', value: 15, color: '#00bf63' },
    { name: 'Arquitectura y Diseño de Espacios', value: 12, color: '#024579' },
    { name: 'Investigación de Mercados', value: 10, color: '#028bbf' }
  ];

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, value, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={16}
        fontWeight="bold"
        style={{
          filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.5))',
          textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
        }}
      >
        {`${value}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vista Panorámica</h1>
          <p className="mt-1 text-sm text-gray-500">
            Análisis general del progreso en empleabilidad de tus estudiantes
          </p>
          <p className="mt-1 text-sm text-gray-400">{dateRange}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeRange('7D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '7D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            7D
          </button>
          <button
            onClick={() => setTimeRange('30D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '30D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            30D
          </button>
          <button
            onClick={() => setTimeRange('90D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '90D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            90D
          </button>
        </div>
      </div>

      {/* Estadísticas principales */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="relative bg-white pt-5 px-4 pb-6 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <div>
              <div className="absolute bg-primary/5 rounded-md p-3">
                <stat.icon className="h-6 w-6 text-primary" aria-hidden="true" />
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">{stat.name}</p>
            </div>
            <div className="ml-16 flex items-baseline mt-1">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p className={`ml-2 flex items-baseline text-sm font-semibold ${
                stat.changeType === 'increase' ? 'text-success' : 'text-red-600'
              }`}>
                {stat.change}
              </p>
            </div>
            <div className="ml-16 mt-1">
              <p className="text-sm text-gray-500">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Estudiantes Activos por Ciclo */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Estudiantes Activos por Ciclo</h2>
          <p className="mt-1 text-sm text-gray-500">Ciclos 2024</p>
          <div className="mt-6" style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={ciclosData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" />
                <Tooltip />
                <Bar dataKey="value" fill="#024579" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Puntaje promedio por carrera */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Puntaje promedio por carrera</h2>
          <p className="mt-1 text-sm text-gray-500">Rendimiento promedio de estudiantes por carrera</p>
          <div className="mt-6" style={{ height: '480px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={puntajePromedio}
                margin={{ top: 20, right: 40, left: 40, bottom: 120 }}
                barSize={50}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  interval={0}
                  height={120}
                  textAnchor="middle"
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const lines = payload.value.split('\n');
                    
                    return (
                      <g transform={`translate(${x},${y + 20})`}>
                        {lines.map((line, index) => (
                          <text
                            key={index}
                            x={0}
                            y={0}
                            dy={index * 20}
                            textAnchor="middle"
                            fill="#666"
                            fontSize={13}
                          >
                            {line}
                          </text>
                        ))}
                      </g>
                    );
                  }}
                />
                <YAxis 
                  domain={[0, 100]}
                  label={{ value: 'Puntaje', angle: -90, position: 'insideLeft', offset: 5 }}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#028bbf">
                  <LabelList dataKey="value" position="top" fontSize={14} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Estudiantes con mejor performance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Estudiantes con mejor performance</h2>
          <div className="mt-6 space-y-4">
            {mejorPerformance.map((estudiante) => (
              <div key={estudiante.name} className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">{estudiante.name}</div>
                  <div className="text-sm font-medium text-gray-500">{estudiante.value}</div>
                </div>
                <div className="mt-2 h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 bg-success rounded-full"
                    style={{ width: `${estudiante.value}%`, backgroundColor: '#00bf63' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Estudiantes con peor performance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Estudiantes con peor performance</h2>
          <div className="mt-6 space-y-4">
            {peorPerformance.map((estudiante) => (
              <div key={estudiante.name} className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">{estudiante.name}</div>
                  <div className="text-sm font-medium text-gray-500">{estudiante.value}</div>
                </div>
                <div className="mt-2 h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 rounded-full"
                    style={{ width: `${estudiante.value}%`, backgroundColor: '#028bbf' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Aspectos a mejorar en Entrevistas */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Aspectos a mejorar en Entrevistas</h2>
          <p className="mt-1 text-sm text-gray-500">Porcentaje de estudiantes que necesitan mejorar</p>
          <div className="mt-6" style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={aspectosEntrevista}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar 
                  dataKey="value" 
                  fill="#024579"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList 
                    dataKey="value" 
                    position="right" 
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Aspectos a mejorar en CVs */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Aspectos a mejorar en CVs</h2>
          <p className="mt-1 text-sm text-gray-500">Porcentaje de estudiantes que necesitan mejorar</p>
          <div className="mt-6" style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={aspectosCV}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis 
                  dataKey="name" 
                  type="category"
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar 
                  dataKey="value" 
                  fill="#028bbf"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList 
                    dataKey="value" 
                    position="right"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Áreas de Interés */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900">Áreas de Interés</h2>
        <p className="mt-1 text-sm text-gray-500">Distribución de intereses profesionales de los estudiantes</p>
        <div className="mt-6" style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={areasInteres}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                innerRadius="45%"
                outerRadius="80%"
                dataKey="value"
                paddingAngle={3}
                strokeWidth={2}
                stroke="#ffffff"
              >
                {areasInteres.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => `${value}%`}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                layout="vertical" 
                align="right"
                verticalAlign="middle"
                wrapperStyle={{
                  paddingLeft: '20px'
                }}
                formatter={(value, entry, index) => (
                  <span style={{ 
                    color: '#4B5563', 
                    fontSize: '14px',
                    fontWeight: '500',
                    paddingLeft: '8px'
                  }}>
                    {value}
                  </span>
                )}
                iconSize={16}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VistaPanoramica; 